import { useGlobalLoaderStore } from '../stores/globalLoader';
import { AccessLevelEnum } from '../enums/accessLevel';
import { useClientStore } from '../stores/client';
import { useCashAdvanceStore } from '../stores/cashAdvance.ts';
import { useInstaWageStore } from '../stores/transactionList.ts';
import { useReadyWageHomeStore } from '../stores/readyWageHome';
import { useLaunchDarklyStore } from '../stores/launchDarkly';
import { useReadyCashViewStatementsStore } from '../stores/readyCashViewStatements';
import { useReadyCashHomeStore } from '../stores/readyCashHome.ts';
import { LaunchDarklyFlagsEnum } from '../enums/launchDarklyFlags';
import router from './index';
import { storeToRefs } from 'pinia';

const controller = new AbortController();
export const redirectNoAccess = (response, queryString) => {
  controller.abort();

  if (response?.redirectUrl) {
    const url = generateUrl(
      response.isAuthenticated,
      response.redirectUrl,
      queryString
    );

    window.location.href = url;
  } else {
    const loader = useGlobalLoaderStore();

    loader.hideLoader();
    router.push({ name: '404' });
  }
};

export const generateUrl = (isAuthenticated, redirectUrl, queryString = '') => {
  return !isAuthenticated
    ? `${redirectUrl}/login.aspx${queryString}`
    : `${redirectUrl + queryString}`;
};

export const hasNoAccess = (routeName, accessLevelStatus) => {
  const routeNames = [
    'attendance-report',
    'attendance-manager',
    'job-request',
    'salary-history',
    'job-request-create',
    'manage-salary-details',
  ];

  if (routeNames.includes(routeName)) {
    return accessLevelStatus === '';
  }

  return accessLevelStatus === AccessLevelEnum.disable;
};

export const redirectToLegacy = (redirectTo) => {
  const clientStore = useClientStore();
  const domain = clientStore.client.parentBaseUrl || '/';

  if (redirectTo == 'dashboard') {
    window.location.href = `${domain}`;
  } else {
    window.location.href = `${domain}/Login.aspx`;
  }
};
export const processAccess = (routes, adminAccessResponse) => {
  routes.map((route) => {
    if (route.meta?.auth) {
      // choose only routes with auth in meta
      processInstaCashAccess(adminAccessResponse);
      processInstaWageAccess(adminAccessResponse);
      processFintechAccess(adminAccessResponse);
    }
  });
};

export const resetPanels = () => {
  const cashAdvanceStore = useCashAdvanceStore();
  const instaWageStore = useInstaWageStore();
  const readyWageStore = useReadyWageHomeStore();
  const readyCashStore = useReadyCashHomeStore();

  instaWageStore.activeRow.index = null;
  instaWageStore.activeRow.details = null;
  instaWageStore.instaWageTransactionDetails.state = false;
  instaWageStore.transactionFiltersDrawer = false;
  instaWageStore.paginationFintech.currentPage = 1;

  readyWageStore.readyWageHomeTransactionListDetails.state = false;
  readyWageStore.paginationFintech.currentPage = 1;
  readyWageStore.readyWageHomeBalanceBreakdown.state = false;

  readyCashStore.isreadyCashHomeTransactionDetailsOpen = false;
  readyCashStore.activeRow.index = null;
  readyCashStore.activeRow.details = null;

  cashAdvanceStore.filtersDrawer = false;
  cashAdvanceStore.activeRow = null;
  cashAdvanceStore.instaCashTransactionDetails.state = false;
  cashAdvanceStore.selectedLoan = null;
  cashAdvanceStore.paginationFintech.currentPage = 1;
};

const processInstaCashAccess = (adminAccessResponse) => {
  const cashAdvanceStore = useCashAdvanceStore();
  cashAdvanceStore.instaCashAccess =
    adminAccessResponse.isInstaCashAdmin === true;
};

const processInstaWageAccess = (adminAccessResponse) => {
  const instaWageStore = useInstaWageStore();
  instaWageStore.instaWageAccess =
    adminAccessResponse.isInstaWageAdmin === true;
};

const processFintechAccess = (adminAccessResponse) => {
  const readyCashStore = useCashAdvanceStore();
  const readyWageHomeStore = useReadyWageHomeStore();

  readyCashStore.hasReadyCash = adminAccessResponse.hasReadyCash;
  readyWageHomeStore.hasReadyWage = adminAccessResponse.hasReadyWage;
  readyCashStore.isReadyCashAdmin = adminAccessResponse.isInstaCashAdmin;
  readyWageHomeStore.isReadyWageAdmin = adminAccessResponse.isInstaWageAdmin;
};

export const checkReRoutes = (router, response, to, queryString) => {
  const cashAdvanceStore = useCashAdvanceStore();
  const readyWageStore = useReadyWageHomeStore();
  const readyCashHomeStore = useReadyCashHomeStore();
  const { LD_ReadyCashHome } = storeToRefs(readyCashHomeStore);
  const { isReadyWageHomeOn } = storeToRefs(readyWageStore);

  if (!cashAdvanceStore.hasReadyCash && !readyWageStore.hasReadyWage) {
    redirectNoAccess(response, queryString);
    return;
  }

  if (to.name !== 'home') return;

  if (cashAdvanceStore.hasReadyCash && cashAdvanceStore.isReadyCashAdmin) {
    router.push({
      name: LD_ReadyCashHome.value
        ? 'readycash-home'
        : 'readycash-cash-advances',
    });
    return;
  }

  if (readyWageStore.hasReadyWage) {
    if (isReadyWageHomeOn.value) {
      router.push({ name: 'readywage-home' });
    } else if (readyWageStore.isReadyWageAdmin) {
      router.push({ name: 'readywage-transaction-list' });
    } else {
      redirectNoAccess(response, queryString);
    }
    return;
  }

  redirectNoAccess(response, queryString);
};

export const setLaunchDarklyFlags = (Flags) => {
  const readyWageStore = useReadyWageHomeStore();
  const launchDarklyStore = useLaunchDarklyStore();
  const RCViewStatementsStore = useReadyCashViewStatementsStore();
  const RCHomeStore = useReadyCashHomeStore();

  const { LDFlags } = storeToRefs(launchDarklyStore);
  const { isReadyWageHomeOn, isReadyWageHomeComputeLateandUTOn } =
    storeToRefs(readyWageStore);
  const { LD_ViewStatements } = storeToRefs(RCViewStatementsStore);
  const { LD_ReadyCashHome } = storeToRefs(RCHomeStore);

  if (!LDFlags.value) {
    isReadyWageHomeOn.value = false;
    isReadyWageHomeComputeLateandUTOn.value = false;
    LD_ViewStatements.value = false;
  } else {
    LDFlags.value = Flags;
    const flagChecks = [
      [isReadyWageHomeOn, LaunchDarklyFlagsEnum.LDReadyWageHome],
      [
        isReadyWageHomeComputeLateandUTOn,
        LaunchDarklyFlagsEnum.LDReadyWageComputeLateandUT,
      ],
      [LD_ViewStatements, LaunchDarklyFlagsEnum.LDReadyCashViewStatements],
      [LD_ReadyCashHome, LaunchDarklyFlagsEnum.LDReadyCashHome],
    ];
    flagChecks.forEach(([ref, flag]) => {
      ref.value = checkLaunchDarklyAccess(LDFlags.value, flag);
    });
  }
};

export const checkLaunchDarklyAccess = (LDFlags, flagEnum) => {
  return Object.hasOwn(LDFlags, flagEnum) && LDFlags[flagEnum] === true;
};
